<template>
  <FlashNotifications ref="flash" />

  <!-- Desktop -->
  <div class="hidden sm:flex sm:flex-col bg-gray-50 dark:bg-[#0d1117]" style="height: calc(100vh - 57px)">

    <div class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center justify-between px-6 py-3.5 gap-4">
        <div>
          <h1 class="text-xl font-bold text-gray-900 dark:text-white leading-none">Release Notes</h1>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            The public changelog at /release-notes. Version numbers are generated for you.
          </p>
        </div>
        <div class="flex items-center gap-2 flex-shrink-0">
          <a href="/release-notes" target="_blank" rel="noopener"
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors">
            View public page
          </a>
          <button type="button" @click="openNew"
            class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
            </svg>
            New release
          </button>
        </div>
      </div>

      <div class="flex items-center gap-2.5 px-6 pb-3.5">
        <div class="relative flex-1 max-w-xs">
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
          </svg>
          <input v-model="search" @input="onSearchInput" placeholder="Search title, version or summary…"
            class="pl-9 pr-3 py-2 w-full text-sm bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" />
        </div>

        <div class="flex items-center gap-1.5">
          <button v-for="option in STATUS_FILTERS" :key="option.value" type="button" @click="setStatus(option.value)"
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg transition-colors"
            :class="status === option.value
              ? 'bg-blue-600 text-white'
              : 'bg-gray-50 dark:bg-[#0d1117] text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800'">
            {{ option.label }}
            <span class="min-w-[20px] h-5 inline-flex items-center justify-center rounded-full text-[11px] font-bold px-1"
              :class="status === option.value ? 'bg-white/20 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'">
              {{ countFor(option.value) }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto px-6 py-5">

      <!-- Loading: mirrors the real table, row for row -->
      <div v-if="loading" class="animate-pulse rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-[#1C2128] overflow-hidden">
        <div class="h-11 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-[#161b22]"></div>
        <div v-for="n in skeletonRows" :key="n" class="flex items-center gap-4 px-5 py-4 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
          <div class="w-16 h-5 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div class="flex-1 h-5 bg-gray-100 dark:bg-gray-800 rounded"></div>
          <div class="w-40 h-5 bg-gray-100 dark:bg-gray-800 rounded"></div>
          <div class="w-24 h-5 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div class="w-28 h-5 bg-gray-100 dark:bg-gray-800 rounded"></div>
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="flex flex-col items-center justify-center py-32 text-center px-4">
        <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
          <svg class="w-7 h-7 text-red-500" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
          </svg>
        </div>
        <h3 class="text-base font-semibold text-gray-900 dark:text-white">Something went wrong</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ error }}</p>
        <button @click="retry" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors">Try again</button>
      </div>

      <!-- Empty -->
      <div v-else-if="records.length === 0" class="flex flex-col items-center justify-center py-32 text-center px-4">
        <div class="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
          <svg class="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.6" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5A3.375 3.375 0 0 0 10.125 2.25H8.25m5.231 13.481L15 17.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/>
          </svg>
        </div>
        <h3 class="text-base font-semibold text-gray-900 dark:text-white">{{ hasFilters ? 'No releases match' : 'No releases yet' }}</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">
          {{ hasFilters ? 'Try a different search or filter.' : 'Write the first one and it appears on the public changelog.' }}
        </p>
        <button v-if="!hasFilters" @click="openNew" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors">New release</button>
      </div>

      <!-- Loaded -->
      <div v-else class="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-[#1C2128] overflow-hidden">
        <table class="w-full text-left">
          <thead class="bg-gray-50 dark:bg-[#161b22] border-b border-gray-100 dark:border-gray-700">
            <tr class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
              <th class="px-5 py-3 w-28">Version</th>
              <th class="px-5 py-3">Title</th>
              <th class="px-5 py-3 w-52">Tags</th>
              <th class="px-5 py-3 w-32">Status</th>
              <th class="px-5 py-3 w-36">Published</th>
              <th class="px-5 py-3 w-44 text-right">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-for="record in records" :key="record.id" class="hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
              <td class="px-5 py-4">
                <span class="font-mono text-sm font-semibold text-gray-900 dark:text-white tabular-nums">v{{ record.version }}</span>
              </td>
              <td class="px-5 py-4">
                <span class="flex items-center gap-1.5">
                  <button type="button" @click="openEdit(record)" class="text-sm font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-left">
                    {{ record.title }}
                  </button>
                  <svg v-if="record.video_id" class="w-3.5 h-3.5 flex-shrink-0 text-gray-300 dark:text-gray-600"
                    viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <title>Has a video</title>
                    <path d="M8 5.14v13.72L19 12 8 5.14z"/>
                  </svg>
                </span>
                <p v-if="record.summary" class="mt-0.5 text-xs text-gray-400 dark:text-gray-500 line-clamp-1">{{ record.summary }}</p>
              </td>
              <td class="px-5 py-4">
                <div class="flex flex-wrap gap-1">
                  <span v-for="tag in record.tags" :key="tag"
                    class="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                    {{ tagLabel(tag) }}
                  </span>
                  <span v-if="!record.tags.length" class="text-xs text-gray-300 dark:text-gray-600">—</span>
                </div>
              </td>
              <td class="px-5 py-4">
                <span class="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold" :class="statusClass(record.display_status)">
                  {{ statusLabel(record.display_status) }}
                </span>
              </td>
              <td class="px-5 py-4 text-sm text-gray-500 dark:text-gray-400">
                {{ record.published_at ? formatDate(record.published_at) : '—' }}
              </td>
              <td class="px-5 py-4">
                <div class="flex items-center justify-end gap-1.5">
                  <a v-if="record.display_status === 'published'" :href="`/release-notes/${record.slug}`" target="_blank" rel="noopener"
                    class="px-2.5 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">View</a>
                  <button type="button" @click="openEdit(record)"
                    class="px-2.5 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">Edit</button>
                  <button v-if="record.display_status === 'draft'" type="button" @click="publishNow(record)" :disabled="working === record.id"
                    class="px-2.5 py-1.5 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors disabled:opacity-50">Publish</button>
                  <button v-else type="button" @click="unpublish(record)" :disabled="working === record.id"
                    class="px-2.5 py-1.5 text-xs font-semibold text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors disabled:opacity-50">Unpublish</button>
                  <button v-if="record.display_status === 'draft'" type="button" @click="askDelete(record)" :disabled="working === record.id"
                    class="px-2.5 py-1.5 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50"
                    :class="pendingDelete === record.id
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'">
                    {{ pendingDelete === record.id ? 'Confirm' : 'Delete' }}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!loading && !error && page.total_pages > 1" class="flex items-center justify-between mt-4">
        <p class="text-xs text-gray-400 dark:text-gray-500">
          Page {{ page.current_page }} of {{ page.total_pages }} · {{ page.total_records }} releases
        </p>
        <div class="flex items-center gap-1.5">
          <button type="button" :disabled="page.current_page <= 1" @click="goToPage(page.current_page - 1)"
            class="px-3 py-1.5 text-sm font-semibold text-gray-600 dark:text-gray-300 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">Previous</button>
          <button type="button" :disabled="page.current_page >= page.total_pages" @click="goToPage(page.current_page + 1)"
            class="px-3 py-1.5 text-sm font-semibold text-gray-600 dark:text-gray-300 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">Next</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Mobile -->
  <div class="sm:hidden bg-gray-50 dark:bg-[#0d1117] min-h-screen">
    <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
      <div class="flex items-center justify-between px-4 h-14">
        <h1 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-none">Release Notes</h1>
        <button type="button" @click="openNew"
          class="px-4 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded-full">New</button>
      </div>
    </div>

    <div class="pb-20">
      <div v-if="loading" class="animate-pulse px-4 pt-6 space-y-3">
        <div v-for="n in skeletonRows" :key="n" class="bg-white dark:bg-[#1C2128] rounded-2xl p-4 space-y-2">
          <div class="w-20 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div class="w-3/4 h-5 bg-gray-100 dark:bg-gray-800 rounded"></div>
        </div>
      </div>

      <div v-else-if="error" class="flex flex-col items-center justify-center py-32 text-center px-4">
        <h3 class="text-base font-semibold text-gray-900 dark:text-white">Something went wrong</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ error }}</p>
        <button @click="retry" class="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold">Try again</button>
      </div>

      <template v-else>
        <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
          {{ page.total_records }} releases
        </p>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
          <button v-for="record in records" :key="record.id" type="button" @click="openEdit(record)"
            class="w-full text-left px-4 py-3.5">
            <div class="flex items-center gap-2">
              <span class="font-mono text-xs font-semibold text-gray-500 dark:text-gray-400">v{{ record.version }}</span>
              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold" :class="statusClass(record.display_status)">
                {{ statusLabel(record.display_status) }}
              </span>
            </div>
            <p class="mt-1 text-sm font-semibold text-gray-900 dark:text-white">{{ record.title }}</p>
            <p v-if="record.published_at" class="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{{ formatDate(record.published_at) }}</p>
          </button>
        </div>
      </template>
    </div>
  </div>

  <ReleaseNoteFormSlideOver
    :open="formOpen"
    :saving="saving"
    :error="formError"
    :release-note="editing"
    :next-versions="nextVersions"
    :attach-image="attachBodyImage"
    @close="formOpen = false"
    @image-error="formError = $event"
    @submit="onFormSubmit" />
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useAdminReleaseNotesStore, RELEASE_TAGS } from 'stores/admin_release_notes.js'
import { getCachedReleaseNotesCount, setCachedReleaseNotesCount } from 'composables/useReleaseNotesCountCache.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import ReleaseNoteFormSlideOver from 'controllers/vue_components/admin/release_notes/ReleaseNoteFormSlideOver.vue'

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'published', label: 'Published' },
]

const STATUS_CLASSES = {
  draft: 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400',
  scheduled: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
  published: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400',
}

const store = useAdminReleaseNotesStore()
const flash = ref(null)

const records = computed(() => store.records)
const loading = computed(() => store.loading)
const error = computed(() => store.error)
const page = computed(() => store.page)
const status = computed(() => store.status)
const nextVersions = computed(() => store.nextVersions)

const search = ref(store.search)
const working = ref(null)
const formOpen = ref(false)
const formError = ref('')
const saving = ref(false)
const editing = ref(null)

// Read synchronously, so the first paint already draws the right number of
// rows rather than a guess that resizes when the response lands.
const skeletonRows = ref(getCachedReleaseNotesCount())

const pendingDelete = ref(null)
let deleteTimer = null

const hasFilters = computed(() => !!store.search || !!store.status || !!store.tag)

function countFor(value) {
  if (value === '') return store.counts.total
  return store.counts[value] ?? 0
}

function tagLabel(value) {
  return RELEASE_TAGS.find((t) => t.value === value)?.label ?? value
}

function statusClass(value) {
  return STATUS_CLASSES[value] ?? STATUS_CLASSES.draft
}

function statusLabel(value) {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

function formatDate(value) {
  return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

let searchTimer = null
function onSearchInput() {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(async () => {
    store.setSearch(search.value)
    await store.fetchList()
  }, 300)
}

async function setStatus(value) {
  store.setStatus(value)
  await store.fetchList()
}

async function goToPage(number) {
  store.goToPage(number)
  await store.fetchList()
}

async function retry() {
  store.invalidate()
  await store.fetchIfStale()
}

function openNew() {
  editing.value = null
  formError.value = ''
  formOpen.value = true
}

function openEdit(record) {
  editing.value = record
  formError.value = ''
  formOpen.value = true
}

// One handler for all four intents the slide-over can emit. Saving and
// publishing are separate requests on purpose: publishing a release that was
// never saved would silently discard the edits still in the form, so the
// attributes always go up first and the status change follows.
async function onFormSubmit({ intent, attributes }) {
  saving.value = true
  formError.value = ''
  try {
    const id = editing.value?.id
    const res = id ? await store.update(id, attributes) : await store.create(attributes)
    if (res.js_error || !res.response.success) {
      formError.value = errorMessage(res, 'Could not save the release.')
      return
    }

    const saved = res.response.data
    if (intent !== 'save') {
      const publishRes = await store.publish(saved.id)
      if (publishRes.js_error || !publishRes.response.success) {
        formError.value = errorMessage(publishRes, 'Saved, but could not publish.')
        await store.fetchList()
        return
      }
    }

    formOpen.value = false
    await store.fetchList()
    flash.value?.show(publishedMessage(intent, saved), 'notice')
  } finally {
    saving.value = false
  }
}

// Handed down to the slide-over as its `attachImage` prop, and from there to
// the editor. Lives here because it may have to create the release first, and
// this component is the only thing that writes to the store.
//
// A blob has to belong to a release: has_many_attached purges with the record,
// so an image attached to nothing would sit in storage forever with no owner
// to clean it up. A brand new release has no id yet, so the first image saves
// the draft and continues with the id that comes back. Everything the author
// has typed so far goes up with it, so nothing is lost and the editor is not
// reset underneath them.
async function attachBodyImage(file, attributes) {
  formError.value = ''
  let id = editing.value?.id

  if (!id) {
    const created = await store.create(attributes)
    if (created.js_error || !created.response.success) {
      formError.value = errorMessage(created, 'Could not save the draft, so the image was not attached.')
      return null
    }
    // Switches the form into edit mode: the version stops being a choice and
    // every later save updates this record rather than creating another.
    editing.value = created.response.data
    id = editing.value.id
    await store.fetchList()
  }

  const res = await store.attachImage(id, file)
  if (res.js_error || !res.response.success) {
    formError.value = errorMessage(res, 'Could not upload that image.')
    return null
  }
  return res.response.data.url
}

// `saved` is the response from the save, before the publish; the record the
// list holds afterwards is the authority on whether it ended up live or
// scheduled, which is what the date field decided.
function publishedMessage(intent, saved) {
  if (intent !== 'publish') return `v${saved.version} saved.`

  const current = store.records.find((r) => r.id === saved.id)
  return current?.display_status === 'scheduled'
    ? `v${saved.version} is scheduled.`
    : `v${saved.version} is live.`
}

// redaxios rejects on any non-2xx, so a render_failure comes back as
// `js_error` with the JSON body on `error.data`, not on `response`. The
// controller puts validation messages in that body's `data` array and the
// headline in `message`.
function errorMessage(res, fallback) {
  const body = res?.js_error ? res.error?.data : res?.response
  const details = body?.data
  if (Array.isArray(details) && details.length) return details.join(', ')
  return body?.message || fallback
}

async function publishNow(record) {
  working.value = record.id
  const res = await store.publish(record.id)
  working.value = null
  if (res.js_error || !res.response.success) {
    flash.value?.show(errorMessage(res, 'Could not publish.'), 'alert')
    return
  }
  await store.fetchList()
  flash.value?.show(`v${record.version} is live.`, 'notice')
}

async function unpublish(record) {
  working.value = record.id
  const res = await store.unpublish(record.id)
  working.value = null
  if (res.js_error || !res.response.success) {
    flash.value?.show(errorMessage(res, 'Could not unpublish.'), 'alert')
    return
  }
  await store.fetchList()
  flash.value?.show(`v${record.version} is back to a draft.`, 'notice')
}

// Two step rather than a modal or a browser confirm(): the first click arms
// the button, the second carries it out, and clicking anything else disarms
// it. Deleting is only ever offered on a draft, which has no public URL.
function askDelete(record) {
  if (pendingDelete.value === record.id) {
    destroy(record)
    return
  }
  pendingDelete.value = record.id
  clearTimeout(deleteTimer)
  deleteTimer = setTimeout(() => { pendingDelete.value = null }, 4000)
}

async function destroy(record) {
  pendingDelete.value = null
  working.value = record.id
  const res = await store.destroy(record.id)
  working.value = null
  if (res.js_error || !res.response.success) {
    flash.value?.show(errorMessage(res, 'Could not delete.'), 'alert')
    return
  }
  await store.fetchList()
  flash.value?.show(`v${record.version} deleted.`, 'notice')
}

onMounted(async () => {
  await store.fetchIfStale()
  if (!store.error) {
    skeletonRows.value = store.records.length
    setCachedReleaseNotesCount(store.records.length)
  }
})
</script>
